// How money reached the business, for a payment recorded by hand.
//
// ── DUPLICATED DEFINITION ─────────────────────────────────────────────────────
// This mirrors PAYMENT_METHODS in supabase/functions/_shared/payment-methods.ts.
// The two must agree, and the API is the authority: it REJECTS a value outside
// its own set, so a method offered only here is a save that always fails.
// Deno's _shared/ cannot be imported from the Vite bundle, which is the same
// reason lib/render.ts restates the render protocol. Listed in /audit Pass 8.
//
// The values are generic on purpose — they name a METHOD, never a provider. The
// gateway option is whichever gateway that business has connected, so it stays
// true for a tenant who is not on the first one.
//
// All three record money that moved OUTSIDE this app. Choosing one is
// bookkeeping: nothing is charged, and no card is touched.

export type PaymentMethodValue = 'payment_gateway' | 'cash' | 'eft'

export interface PaymentMethodOption {
  value: PaymentMethodValue
  label: string
  hint: string
}

export const PAYMENT_METHODS: readonly PaymentMethodOption[] = [
  {
    value: 'payment_gateway',
    label: 'Payment gateway',
    hint: 'Taken on the card gateway connected to this business, outside this app.',
  },
  { value: 'cash', label: 'Cash', hint: 'Handed over in person.' },
  { value: 'eft', label: 'Bank transfer (EFT)', hint: 'Paid into the business account.' },
] as const

/**
 * A human label for ANY stored value, including the ones this app never writes:
 * 'card_worldline' from the gateway settlement path, and whatever the Momence
 * import recorded for four thousand historical sales.
 *
 * An unrecognised value is title-cased rather than bucketed as "Other" — a
 * method nobody has used for a year is still the truest record of how that money
 * arrived, and a catch-all would make the historical ledger less accurate than
 * the data underneath it.
 */
export function describeMethod(stored: string | null | undefined): string {
  const value = (stored ?? '').trim().toLowerCase().replace(/[\s_-]+/g, ' ')
  if (!value || value === 'unknown') return 'Not recorded'

  // Kept in step with ALIASES in the shared module. A gateway capture confirmed
  // by the webhook and one keyed in by hand are the same money to a reader, so
  // they carry one label.
  if (['payment gateway', 'card worldline', 'card', 'creditcard', 'credit card', 'stripe', 'online', 'terminal'].includes(value)) {
    return 'Payment gateway'
  }
  if (value === 'cash') return 'Cash'
  if (['eft', 'banktransfer', 'bank transfer', 'directdeposit', 'direct deposit', 'transfer'].includes(value)) {
    return 'Bank transfer (EFT)'
  }
  return value.replace(/\b\w/g, (c) => c.toUpperCase())
}
